import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toastError } from '../../../utils/toast';
import { GROCERY_PRODUCT_CATEGORIES } from '../../../constants/groceryCategories';
import API_BASE_URL from '../../../config/api';

const OTHER_CATEGORY_VALUE = '__other__';

const GroceryShopProductModal = ({ isOpen, onClose, product, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    origin_country: '',
    origin_region: '',
    is_african: false,
    price: '',
    stock_quantity: '0',
    unit: 'piece',
    image_url: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [useCustomCategory, setUseCustomCategory] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (product) {
      const category = product.category || '';
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category,
        origin_country: product.origin_country || '',
        origin_region: product.origin_region || '',
        is_african: !!product.is_african,
        price: product.price != null ? String(product.price) : '',
        stock_quantity: product.stock_quantity != null ? String(product.stock_quantity) : '0',
        unit: product.unit || 'piece',
        image_url: product.image_url || '',
        is_active: product.is_active !== false,
      });
      setUseCustomCategory(!!category && !GROCERY_PRODUCT_CATEGORIES.includes(category));
    } else {
      setFormData({
        name: '',
        description: '',
        category: '',
        origin_country: '',
        origin_region: '',
        is_african: false,
        price: '',
        stock_quantity: '0',
        unit: 'piece',
        image_url: '',
        is_active: true,
      });
      setUseCustomCategory(false);
    }
    setErrors({});
  }, [product, isOpen]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, image: 'Le fichier doit être une image' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: "L'image ne doit pas dépasser 5MB" }));
      return;
    }

    try {
      setUploadingImage(true);
      setErrors((prev) => ({ ...prev, image: null }));

      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'grocery-products');

      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/management/upload/image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: uploadData,
      });

      if (!response.ok) throw new Error('Erreur lors de l\'upload');

      const result = await response.json();
      setFormData((prev) => ({ ...prev, image_url: result.image_url }));
    } catch (error) {
      console.error('Erreur upload:', error);
      setErrors((prev) => ({ ...prev, image: "Erreur lors de l'upload de l'image" }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (formData.price === '' || parseFloat(formData.price) < 0) {
      newErrors.price = 'Le prix doit être supérieur ou égal à 0';
    }
    if (formData.stock_quantity !== '' && parseInt(formData.stock_quantity, 10) < 0) {
      newErrors.stock_quantity = 'Le stock doit être supérieur ou égal à 0';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError('Veuillez corriger les champs : nom et prix sont obligatoires.');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        name: formData.name.trim(),
        description: formData.description || null,
        category: formData.category || null,
        origin_country: formData.origin_country || null,
        origin_region: formData.origin_region || null,
        is_african: !!formData.is_african,
        price: parseFloat(formData.price),
        stock_quantity: formData.stock_quantity === '' ? 0 : parseInt(formData.stock_quantity, 10),
        unit: formData.unit || 'piece',
        image_url: formData.image_url || null,
        is_active: !!formData.is_active,
      };

      if (product) {
        await onSuccess(product.id, submitData);
      } else {
        await onSuccess(submitData);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du produit catalogue:', error);
      setErrors({ general: 'Erreur lors de la sauvegarde' });
      toastError(error.response?.data?.detail || 'Erreur lors de la sauvegarde du produit.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">
            {product ? 'Modifier le produit catalogue' : 'Ajouter un produit catalogue'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {errors.general}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom du produit *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625] ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Farine de manioc"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                placeholder="Décrivez ce produit..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prix (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-[#054625] ${
                    errors.price ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="4.99"
                />
                {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <select
                  value={useCustomCategory ? OTHER_CATEGORY_VALUE : formData.category}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === OTHER_CATEGORY_VALUE) {
                      setUseCustomCategory(true);
                      setFormData((prev) => ({ ...prev, category: '' }));
                    } else {
                      setUseCustomCategory(false);
                      setFormData((prev) => ({ ...prev, category: value }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {GROCERY_PRODUCT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value={OTHER_CATEGORY_VALUE}>Autre…</option>
                </select>
                {useCustomCategory && (
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                    placeholder="Précisez la catégorie"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stock</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData((prev) => ({ ...prev, stock_quantity: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625] ${
                    errors.stock_quantity ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                />
                {errors.stock_quantity && <p className="mt-1 text-sm text-red-600">{errors.stock_quantity}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unité</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                  placeholder="piece, kg, L..."
                />
              </div>

              <div className="flex items-end pb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_african"
                    checked={formData.is_african}
                    onChange={(e) => setFormData((prev) => ({ ...prev, is_african: e.target.checked }))}
                    className="h-4 w-4 text-green-700 focus:ring-green-700 border-gray-300 rounded"
                  />
                  <label htmlFor="is_african" className="text-sm text-gray-700">Produit africain</label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pays d&apos;origine</label>
                <input
                  type="text"
                  value={formData.origin_country}
                  onChange={(e) => setFormData((prev) => ({ ...prev, origin_country: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                  placeholder="Ex: Cameroun"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Région d&apos;origine</label>
                <input
                  type="text"
                  value={formData.origin_region}
                  onChange={(e) => setFormData((prev) => ({ ...prev, origin_region: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625]"
                  placeholder="Ex: Afrique centrale"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image du produit</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-700 focus:border-[#054625] file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
              />
              {uploadingImage && (
                <div className="mt-2 flex items-center gap-2 text-green-700 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#054625]" />
                  Upload en cours...
                </div>
              )}
              {errors.image && <p className="mt-1 text-sm text-red-600">{errors.image}</p>}
              {formData.image_url && (
                <div className="mt-3 border border-gray-200 rounded-lg p-4">
                  <img
                    src={formData.image_url}
                    alt="Aperçu"
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="grocery_product_is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 text-green-700 focus:ring-green-700 border-gray-300 rounded"
              />
              <label htmlFor="grocery_product_is_active" className="ml-2 text-sm text-gray-700">
                Produit actif (visible sur la fiche publique)
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Enregistrement...' : (product ? 'Modifier' : 'Ajouter')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroceryShopProductModal;
